import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ChevronDown, Shuffle } from 'lucide-react';
import { listRoles, createGame, assignRoles } from '@/api/mafia';
import { toast } from 'sonner';
import HostLayout from '@/components/mafia/HostLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const TEAMS = [
  { k: 'red',   label: 'Красная',  short: 'R', bg: 'bg-rose-500/15',   border: 'border-rose-500/40',   text: 'text-rose-300',  active: 'bg-rose-500 text-white border-rose-500' },
  { k: 'black', label: 'Чёрная',   short: 'B', bg: 'bg-zinc-700/30',  border: 'border-zinc-500/40',   text: 'text-zinc-200',  active: 'bg-zinc-900 text-white border-zinc-700' },
  { k: 'gray',  label: 'Серая',    short: 'G', bg: 'bg-stone-500/15', border: 'border-stone-400/40',  text: 'text-stone-300', active: 'bg-stone-500 text-white border-stone-500' },
];

const UNLIMITED_ROLE_NAMES = new Set(['Рядовая мафия', 'Мирный житель']);

const PLAYER_COUNT_RANGE = Array.from({ length: 16 }, (_, i) => i + 10); // 10..25

export default function MafiaNewGamePage() {
  const navigate = useNavigate();
  const { host, loading } = useMafiaAuth();
  const [roles, setRoles] = useState([]);
  const [count, setCount] = useState(10);
  const [seats, setSeats] = useState([]); // [{ name, team, roleId }]
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);
  useEffect(() => { listRoles().then(d => setRoles(d.roles || [])); }, []);

  // Sync seat array with count
  useEffect(() => {
    setSeats((prev) => {
      const next = [...prev];
      while (next.length < count) next.push({ name: '', team: '', roleId: '' });
      next.length = count;
      return next;
    });
  }, [count]);

  const rolesByTeam = useMemo(() => {
    const m = { red: [], black: [], gray: [] };
    roles.forEach(r => { if (m[r.team]) m[r.team].push(r); });
    return m;
  }, [roles]);

  // Roles already taken by OTHER seats (single-use enforcement).
  // Excludes "Рядовая мафия" and "Мирный житель" which may be repeated.
  const usedRoleIds = useMemo(() => {
    const used = new Set();
    seats.forEach((s) => {
      if (!s.roleId) return;
      const role = roles.find(r => r.id === s.roleId);
      if (!role) return;
      if (UNLIMITED_ROLE_NAMES.has(role.name)) return;
      used.add(s.roleId);
    });
    return used;
  }, [seats, roles]);

  const setSeat = (i, patch) => setSeats(s => s.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  // Click team → if same, clear; else set & clear role if it doesn't fit new team
  const pickTeam = (i, team) => {
    setSeats(s => s.map((x, idx) => {
      if (idx !== i) return x;
      if (x.team === team) return { ...x, team: '', roleId: '' };
      const currentRole = roles.find(r => r.id === x.roleId);
      const keepRole = currentRole && currentRole.team === team ? x.roleId : '';
      return { ...x, team, roleId: keepRole };
    }));
  };

  const randomFill = () => {
    // Build a balanced team plan then fill roles respecting single-use rule.
    const n = count;
    const blackCount = n <= 11 ? 2 : n <= 15 ? 3 : n <= 19 ? 4 : 5;
    const grayCount = n >= 14 ? 1 : 0;
    const redCount = n - blackCount - grayCount;

    const teamPlan = [
      ...Array(redCount).fill('red'),
      ...Array(blackCount).fill('black'),
      ...Array(grayCount).fill('gray'),
    ];
    for (let i = teamPlan.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [teamPlan[i], teamPlan[j]] = [teamPlan[j], teamPlan[i]];
    }

    // Take a working copy of each team's role pool and lift "unlimited" roles to a fallback bucket.
    const remainingByTeam = {
      red: (rolesByTeam.red || []).filter(r => !UNLIMITED_ROLE_NAMES.has(r.name)),
      black: (rolesByTeam.black || []).filter(r => !UNLIMITED_ROLE_NAMES.has(r.name)),
      gray: (rolesByTeam.gray || []).filter(r => !UNLIMITED_ROLE_NAMES.has(r.name)),
    };
    const unlimitedByTeam = {
      red: (rolesByTeam.red || []).find(r => r.name === 'Мирный житель'),
      black: (rolesByTeam.black || []).find(r => r.name === 'Рядовая мафия'),
      gray: null,
    };

    const picks = teamPlan.map((team) => {
      const bucket = remainingByTeam[team];
      if (bucket.length > 0) {
        // Pick a random unique role from this team
        const idx = Math.floor(Math.random() * bucket.length);
        const [chosen] = bucket.splice(idx, 1);
        return chosen.id;
      }
      // Fall back to the team's unlimited role (e.g., Мирный житель for red)
      return unlimitedByTeam[team]?.id || '';
    });

    setSeats(s => s.map((x, i) => ({
      ...x,
      team: teamPlan[i],
      roleId: picks[i],
    })));
    toast.success('Roles randomly assigned');
  };

  const allAssigned = seats.length > 0 && seats.every(s => s.roleId);
  const assignedCount = seats.filter(s => s.roleId).length;

  const startGame = async () => {
    if (!allAssigned) { toast.error('Assign a role to every seat first'); return; }
    setCreating(true);
    try {
      const names = seats.map((s, i) => (s.name || '').trim() || `Игрок ${i + 1}`);
      const created = await createGame({ player_count: count, player_names: names, role_setup: {} });
      const assignments = {};
      seats.forEach((s, i) => { assignments[i + 1] = s.roleId; });
      await assignRoles(created.id, { mode: 'manual', assignments });
      toast.success('Game created');
      navigate(`/mafia/game/${created.id}`);
    } catch (e) {
      toast.error('Could not create game');
    } finally { setCreating(false); }
  };

  if (loading) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  return (
    <HostLayout
      title="Новая игра"
      subtitle="Быстрая настройка стола"
      actions={
        <>
          <button onClick={randomFill} disabled={!roles.length} className="ms-btn ms-btn-secondary ms-btn-sm" data-testid="new-random-fill">
            <Shuffle size={12} /> Случайно
          </button>
          <button onClick={startGame} disabled={!allAssigned || creating} data-testid="new-start-btn" className="ms-btn ms-btn-primary disabled:opacity-50">
            <Play size={13} /> {creating ? 'Создание…' : 'Начать игру'}
          </button>
        </>
      }
    >
      {/* Player count */}
      <section className="ms-card p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="new-count-section">
        <div>
          <p className="ms-kicker">Шаг 1</p>
          <h2 className="ms-display text-lg mt-1">Сколько игроков?</h2>
          <p className="text-xs ms-text-2 mt-1">Выберите количество мест, затем назначьте роли ниже.</p>
        </div>
        <div className="relative">
          <select
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
            data-testid="new-count-select"
            className="ms-input appearance-none pr-9 text-base font-semibold tabular-nums cursor-pointer"
            style={{ minWidth: '8rem' }}
          >
            {PLAYER_COUNT_RANGE.map(n => (
              <option key={n} value={n}>{n} игроков</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 ms-text-3 pointer-events-none" />
        </div>
      </section>

      {/* Progress */}
      <div className="flex items-center justify-between mb-3">
        <p className="ms-kicker">Шаг 2 · назначить роли</p>
        <span className="text-xs ms-text-3 tabular-nums">{assignedCount} / {count} назначено</span>
      </div>

      {/* Seat rows */}
      <motion.section
        className="ms-card overflow-hidden"
        layout
        data-testid="new-seats-list"
      >
        <div className="ms-divide">
          {seats.map((s, i) => (
            <SeatRow
              key={i}
              index={i}
              seat={s}
              rolesByTeam={rolesByTeam}
              usedRoleIds={usedRoleIds}
              onName={(name) => setSeat(i, { name })}
              onTeam={(team) => pickTeam(i, team)}
              onRole={(roleId) => setSeat(i, { roleId })}
            />
          ))}
        </div>
      </motion.section>
    </HostLayout>
  );
}

function SeatRow({ index, seat, rolesByTeam, usedRoleIds, onName, onTeam, onRole }) {
  const teamRoles = seat.team ? (rolesByTeam[seat.team] || []) : [];
  // Hide roles taken by OTHER seats. Always allow the current seat's own role and unlimited ones.
  const availableRoles = teamRoles.filter(r =>
    r.id === seat.roleId || !usedRoleIds.has(r.id)
  );
  const teamCfg = TEAMS.find(t => t.k === seat.team);
  return (
    <div
      className="grid grid-cols-[2.5rem_1fr_auto_minmax(11rem,16rem)] items-center gap-3 px-4 py-3 hover:bg-[var(--ms-surface-2)] transition-colors"
      data-testid={`seat-row-${index + 1}`}
    >
      {/* Seat number */}
      <span
        className="ms-display text-base tabular-nums text-center font-semibold"
        style={{ color: seat.roleId ? 'var(--ms-text-primary)' : 'var(--ms-text-muted)' }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* Name input */}
      <input
        value={seat.name}
        onChange={(e) => onName(e.target.value)}
        placeholder={`Игрок ${index + 1}`}
        data-testid={`seat-name-${index + 1}`}
        className="ms-input"
      />

      {/* Team buttons */}
      <div className="flex items-center gap-1" data-testid={`seat-team-${index + 1}`}>
        {TEAMS.map(t => {
          const active = seat.team === t.k;
          return (
            <button
              key={t.k}
              onClick={() => onTeam(t.k)}
              data-testid={`seat-${index + 1}-team-${t.k}`}
              title={t.label}
              className={`w-9 h-9 flex items-center justify-center text-xs font-bold border transition-all ${
                active ? t.active : `${t.bg} ${t.border} ${t.text} hover:opacity-100 opacity-70`
              }`}
              style={{ borderRadius: 'var(--ms-radius-sm)' }}
            >
              {t.short}
            </button>
          );
        })}
      </div>

      {/* Role dropdown */}
      <div className="relative">
        <select
          value={seat.roleId}
          onChange={(e) => onRole(e.target.value)}
          disabled={!seat.team}
          data-testid={`seat-role-${index + 1}`}
          className="ms-input appearance-none pr-9 text-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <option value="">{seat.team ? `Choose role…` : 'Pick team first'}</option>
          {availableRoles.map(r => (
            <option key={r.id} value={r.id}>{r.icon} {r.name}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 ms-text-3 pointer-events-none" />
        {teamCfg && seat.roleId && (
          <div
            className="absolute -left-1 top-1/2 -translate-y-1/2 w-0.5 h-5"
            style={{ background: teamCfg.k === 'red' ? '#F43F5E' : teamCfg.k === 'black' ? '#A1A1AA' : '#D6D3D1' }}
          />
        )}
      </div>
    </div>
  );
}
